// Util file to help override the default values of the feature gate bootstrap values for lambda edge work
import { getSelectorsByUserAgent } from 'react-device-detect';
import { UserAttributes } from 'src/feature-gates/bootstrapper';
import { parseRoute } from 'src/wac-routes';

import { ATL_COHORT } from '@/analytics/atl-analytics/cohort';
import { Cookie } from '@/utils/cookie';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { sanitizeQueryParams } from '@/utils/sanitize-query-params';
import { isSourcetreeHostname } from '@/utils/vanity-hostname';

const DEVICE_TYPE = 'deviceType';

// TODO: GWP-5370 Remove this and refactor getCustomAttributes in bootstrapper.ts instead
/**
 * Does same thing as getCustomAttributes from bootstrapper.ts but for the client side. Used to help override the default values of the feature gate bootstrap values for lambda edge work
 *
 * @returns { UserAttributes }
 * */
export const getCustomAttributesOnClient = (): UserAttributes => {
	const data: Partial<UserAttributes> = {};
	const atlCohort = Cookie.readCookie(ATL_COHORT);
	if (atlCohort) {
		data[ATL_COHORT] = JSON.parse(atlCohort).bucketAll.index;
	}
	const userAgent = navigator.userAgent;
	const { isTablet, isMobile, isDesktop, browserName, browserVersion, osName } =
		getSelectorsByUserAgent(userAgent) ?? {};
	if (isTablet) {
		data[DEVICE_TYPE] = 'tablet';
	} else if (isMobile) {
		data[DEVICE_TYPE] = 'mobile';
	} else if (isDesktop) {
		data[DEVICE_TYPE] = 'desktop';
	} else {
		data[DEVICE_TYPE] = 'other';
	}
	data.platform = 'uip';
	data.browserName = browserName;
	data.browserVersion = browserVersion;
	data.browserOS = osName;
	// Scrub Personal Data from the query string before exposing it to Statsig
	// as a user attribute. See sanitize-query-params.ts and the PD standard
	// at https://hello.atlassian.net/wiki/spaces/PMP/pages/139161617.
	data.queryParams = sanitizeQueryParams(location.search);
	data.requestPath = location.pathname;
	data.referer = document.referrer;
	data.locale = parseRoute(location).locale;
	const cloudSessionToken =
		Cookie.readCookie('cloud.session.token') || Cookie.readCookie('cloud.session.token.stg');
	data.loginUserDetected = cloudSessionToken !== null;
	// @ts-expect-error edgeTraceId is a WAC-specific attribute not declared in UserAttributes.
	data.edgeTraceId = Cookie.readCookie('X-Experiments-Trace-Id');
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
	return data as UserAttributes;
};

interface FeatureGateBootstrapValues {
	identifiers?: {
		analyticsAnonymousId?: string;
		atlassianAccountId?: string;
	};
	experimentValues?: {
		user?: {
			customIDs?: {
				analyticsAnonymousId?: string;
				atlassianAccountId?: string;
			};
			custom?: Record<string, string | number | boolean>;
		};
		evaluated_keys?: {
			customIDs?: {
				analyticsAnonymousId?: string;
				atlassianAccountId?: string;
			};
		};
	};
	customAttributes?: Record<string, string | number | boolean>;
}

export function overrideAjsAnonIdFromCookie(): void {
	try {
		const ajsAnonIdFromCookie = Cookie.readCookie('ajs_anonymous_id');
		if (!ajsAnonIdFromCookie) {
			return; // Early return if no cookie exists
		}

		const featureGateBootstrapValues =
			window.featureGateBootstrapValues as FeatureGateBootstrapValues;
		if (!featureGateBootstrapValues) {
			console.warn('featureGateBootstrapValues is not defined');
			return;
		}

		try {
			const decodedAjsAnonId = decodeURIComponent(ajsAnonIdFromCookie).replace(/['"]+/g, '');

			// Update identifiers
			if (featureGateBootstrapValues.identifiers) {
				featureGateBootstrapValues.identifiers.analyticsAnonymousId = decodedAjsAnonId;
			} else {
				console.warn(
					'Could not set analyticsAnonymousId in identifiers - Object path is incomplete.',
				);
			}

			// Update experimentValues.user.customIDs
			if (featureGateBootstrapValues.experimentValues?.user?.customIDs) {
				featureGateBootstrapValues.experimentValues.user.customIDs.analyticsAnonymousId =
					decodedAjsAnonId;
			} else {
				console.warn(
					'Could not set analyticsAnonymousId inside experimentValues.user.customIDs - Object path is incomplete.',
				);
			}

			// Update experimentValues.evaluated_keys.customIDs
			if (featureGateBootstrapValues.experimentValues?.evaluated_keys?.customIDs) {
				featureGateBootstrapValues.experimentValues.evaluated_keys.customIDs.analyticsAnonymousId =
					decodedAjsAnonId;
			} else {
				console.warn(
					'Could not set analyticsAnonymousId inside experimentValues.evaluated_keys - Object path is incomplete.',
				);
			}
		} catch (decodeError) {
			console.error('Error decoding or setting anonymous ID:', decodeError);
		}
	} catch (error) {
		console.error('Error in overrideAjsAnonIdFromCookie:', error);
	}
}

export function overrideCustomAttributes(): void {
	try {
		const newCustomAttributes = getCustomAttributesOnClient();
		if (!newCustomAttributes) {
			console.warn('No custom attributes found to override');
			return;
		}

		const featureGateBootstrapValues =
			window.featureGateBootstrapValues as FeatureGateBootstrapValues;
		if (!featureGateBootstrapValues) {
			console.warn('featureGateBootstrapValues is not defined');
			return;
		}

		try {
			// Update customAttributes
			if (featureGateBootstrapValues.customAttributes) {
				Object.assign(featureGateBootstrapValues.customAttributes, newCustomAttributes);
			} else {
				console.warn(
					'Could not set customAttributes inside featureGateBootstrapValues.customAttributes - Object path is incomplete.',
				);
			}

			// Update experimentValues.user.custom
			if (featureGateBootstrapValues?.experimentValues?.user?.custom) {
				Object.assign(featureGateBootstrapValues.experimentValues.user.custom, newCustomAttributes);
			} else {
				console.warn(
					'Could not set customAttributes inside experimentValues.user.custom - Object path is incomplete.',
				);
			}
		} catch (assignError) {
			console.error('Error assigning custom attributes:', assignError);
		}
	} catch (error) {
		console.error('Error in overrideCustomAttributes:', error);
	}
}

const baseEdgePaths = ['/', '/software/jira/product-discovery'];

// Pre-compile the regex for customer paths
const CUSTOMERS_REGEX = new RegExp(`^(/[a-z]{2})?/customers(/.*)?$`);
const CONFLUENCE_TEMPLATES_DETAILS_REGEX = new RegExp(
	`^(/[a-z]{2})?/software/confluence/templates/[^/]+$`,
);
const CONFLUENCE_TEMPLATES_EXCLUDED_REGEX = new RegExp(
	`^(/[a-z]{2})?/software/confluence/templates/(categories|search)$`,
);
const WORK_MANAGEMENT_REGEX = new RegExp(`^(/[a-z]{2})?/work-management(/.*)?$`);
const GIT_REGEX = new RegExp(`^(/[a-z]{2})?/git(/.*)?$`);
const ITSM_REGEX = new RegExp(`^(/[a-z]{2})?/itsm(/.*)?$`);
const INCIDENT_MANAGEMENT_REGEX = new RegExp(`^(/[a-z]{2})?/incident-management(/.*)?$`);
const INCIDENT_MANAGEMENT_EXCLUDED_REGEX = new RegExp(
	`^(/[a-z]{2})?/incident-management/(get-the-handbook|incident-communication/handbook|20[0-9]{2}-state-of-incident-management)(/.*)?$`,
);
const AGILE_REGEX = new RegExp(`^(/[a-z]{2})?/agile(/.*)?$`);
const AGILE_EXCLUDED_REGEX = new RegExp(
	`^(/[a-z]{2})?/agile(/agile-at-scale/beyond-the-basics-whitepaper|/articles|/about(/.*)?)$`,
);

// Three exact paths for edge caching (no locale variants, no trailing wildcards)
const EDGE_CACHED_EXACT_PATHS: readonly string[] = [
	'/software/jira/service-management/aiops',
	'/collections/service/shatter-the-service-quo',
	'/collections/service/ai',
];

function generateEdgeCachingPaths(edgePaths: string[]): string[] {
	const locales = ['br', 'de', 'en', 'es', 'fr', 'it', 'ja', 'ko', 'nl', 'pl', 'ru', 'zh'];
	const allPaths = new Set<string>();

	edgePaths.forEach((path) => {
		// Add the original path
		allPaths.add(path);

		// For root ("/"), just add /ja, /br, etc.
		if (path === '/') {
			locales.forEach((locale) => {
				allPaths.add(`/${locale}`);
			});
		} else {
			// Add localized versions like /ja/customers
			locales.forEach((locale) => {
				allPaths.add(`/${locale}${path}`);
			});
		}
	});

	return Array.from(allPaths).sort();
}

export const edgeCachingPaths = generateEdgeCachingPaths(baseEdgePaths);

/**
 * Checks if the current request should be edge cached for sourcetreeapp.com domain
 * based on the edge-cache-sourcetreapp feature flag.
 */
export function isSourcetreeAppEdgeCachingEnabled(): boolean {
	return (
		typeof window !== 'undefined' &&
		isSourcetreeHostname(window.location.hostname) &&
		checkFeatureFlagGate(featureFlags.edgeCacheSourcetreapp)
	);
}

export function isEdgeCachingPath(location: Pick<Location, 'pathname' | 'hostname'>): boolean {
	// Check if the request is from sourcetreeapp.com — all paths are edge cached
	if (isSourcetreeHostname(location.hostname)) {
		return checkFeatureFlagGate(featureFlags.edgeCacheSourcetreapp);
	}

	// First check if it's a customer path (fast regex check)
	if (CUSTOMERS_REGEX.test(location.pathname)) {
		return true;
	}

	// Explicit exclusions for templates categories/search pages
	if (CONFLUENCE_TEMPLATES_EXCLUDED_REGEX.test(location.pathname)) {
		return false;
	}

	if (CONFLUENCE_TEMPLATES_DETAILS_REGEX.test(location.pathname)) {
		return true;
	}

	// Check work-management paths
	if (WORK_MANAGEMENT_REGEX.test(location.pathname)) {
		return true;
	}

	// Check git paths
	if (GIT_REGEX.test(location.pathname)) {
		return true;
	}

	// Check ITSM paths
	if (ITSM_REGEX.test(location.pathname)) {
		return true;
	}

	// Explicit exclusions for incident-management paths
	if (INCIDENT_MANAGEMENT_EXCLUDED_REGEX.test(location.pathname)) {
		return false;
	}

	// Check incident-management paths
	if (INCIDENT_MANAGEMENT_REGEX.test(location.pathname)) {
		return true;
	}

	// Explicit exclusions for agile paths
	if (AGILE_EXCLUDED_REGEX.test(location.pathname)) {
		return false;
	}

	// Check agile paths
	if (AGILE_REGEX.test(location.pathname)) {
		return true;
	}

	// Check exact paths for edge caching
	if (EDGE_CACHED_EXACT_PATHS.includes(location.pathname)) {
		return true;
	}

	// Then check other paths
	return edgeCachingPaths.includes(location.pathname);
}
